import React, { useEffect, useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Offcanvas from 'react-bootstrap/Offcanvas';
import { ITotalSalesItem, PAYMETHOD, TRANSACTION } from '@dataTypes/totalSalesTypes';
import { useRecoilValue } from 'recoil';
import { format } from 'date-fns';
import storeInfo from '../../recoil/storeInfo/atom';
import { IVanCancelRequest, IVanCancelResponse } from '@dataTypes/vanCancelTypes';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import Modal from 'react-bootstrap/esm/Modal';
import { AUTH } from '@dataTypes/authTypes';
import Cookies from 'js-cookie';
import axios from 'axios';
import { TOTALSALES } from '@components/totalSales/queryKey/queryKey';
import { useGetTotalSalesLists } from '@hooks/totalSales/useGetTotalSalesLists';
import { perMachineSummarySalesData, perMachineTotalSalesData } from '@dataTypes/perMachineTypes';
import TotalSalesLists from '@components/totalSales/TotalSalesLists';
import TotalSalesListsSkeleton from '@components/totalSales/TotalSalesListsSkeleton';
import { useInView } from 'react-intersection-observer';
import { vanCancel, vanCancelStatus } from '@apis/vancancel';
import { GoTriangleUp } from 'react-icons/go';
import { formatPayMethodLabel } from '@utils/mileageDisplay';

interface PaymentHistoryModalProps {
  showPaymentHistoryModal: boolean;
  showPaymentHistoryModalHandleClose: () => void;
  machineData: perMachineTotalSalesData | perMachineSummarySalesData | undefined;
  startDate: Date;
  endDate: Date;
  showMileage?: boolean;
}

const PaymentHistoryModal = ({
  showPaymentHistoryModal,
  showPaymentHistoryModalHandleClose,
  startDate,
  endDate,
  machineData,
  showMileage = true,
}: PaymentHistoryModalProps) => {
  const [showVanCancelModal, setShowVanCancelModal] = useState(false);
  const [cancelData, setCancelData] = useState<ITotalSalesItem | null>(null);
  const [errorText, setErrorText] = useState('');
  const storeInfoState = useRecoilValue(storeInfo);
  const queryClient = useQueryClient();
  const authority = Cookies.get('smartlab_authority');
  const { ref, inView } = useInView();
  const [openDetailId, setOpenDetailId] = useState<number | null>(null);

  useEffect(() => {
    if (showPaymentHistoryModal && machineData?.catId) {
      setSearchTrigger(true);
    }
  }, [showPaymentHistoryModal, machineData?.catId]);

  const {
    totalSalesLists,
    isFetchingNextPage,
    isError,
    isFetching,
    isTotalSalesLoading,
    fetchNextPage,
    setSearchTrigger,
    hasNextPage,
  } = useGetTotalSalesLists({
    startDate: format(startDate, 'yyMMdd'),
    endDate: format(new Date(endDate), 'yyMMdd'),
    catId: machineData?.catId || '',
    method: PAYMETHOD.TOTAL,
    storeId: storeInfoState.currentStoreId,
  });

  const visibleSalesLists = useMemo(() => {
    if (!totalSalesLists?.length) return totalSalesLists;
    if (showMileage) return totalSalesLists;
    return totalSalesLists.filter((list) => list.method !== PAYMETHOD.CASH);
  }, [totalSalesLists, showMileage]);

  const cardData = visibleSalesLists?.filter((list) => list.method === PAYMETHOD.CARD);

  const vanCancelStatusQueries = useQueries({
    queries:
      cardData?.map((item) => ({
        queryKey: [TOTALSALES.VANCANCELSTATUS, item.id],
        queryFn: () => vanCancelStatus(item.id),
        enabled: !!item.id && showPaymentHistoryModal,
      })) ?? [],
  });

  const { mutateAsync: vanCancelAsync } = useMutation({
    mutationFn: (data: IVanCancelRequest) => vanCancel(data),
  });

  const formatTransactionDate = (transactionDate: string) => {
    const datePart = transactionDate.slice(0, 6); // "240601"
    const timePart = transactionDate.slice(6); // "000002"

    // 날짜 포맷팅
    const year = datePart.slice(0, 2); // "24"
    const month = datePart.slice(2, 4); // "06"
    const day = datePart.slice(4, 6); // "01"
    const formattedDate = `${year}-${month}-${day}`; // "24-06-01"

    // 시간 포맷팅
    const hours = timePart.slice(0, 2); // "00"
    const minutes = timePart.slice(2, 4); // "00"
    const seconds = timePart.slice(4, 6); // "02"
    const formattedTime = `${hours}:${minutes}:${seconds}`; // "00:00:02"

    return [formattedDate, formattedTime];
  };

  const formattedTransaction = (method: TRANSACTION) => {
    switch (method) {
      case TRANSACTION.A:
        return '승인';
      case TRANSACTION.C:
        return '취소';
      case TRANSACTION.E:
        return '에러';
      default:
        return '알 수 없음';
    }
  };

  const addCommas = (number: number) => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const handleVanCancel = async (id: number, vanKey: string) => {
    try {
      const result = await vanCancelAsync({ kisTotalSalesId: id, vanKey });
      if ('resultCode' in result && result.resultCode === '0000') {
        setShowVanCancelModal(false);
        queryClient.invalidateQueries({ queryKey: [TOTALSALES.VANCANCELSTATUS] });
      } else {
        setErrorText((result as IVanCancelResponse).resultMessage || '알 수 없는 오류');
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorMessage = error.response?.data?.message || error.message || '퇴실 실패';
        setErrorText(errorMessage);
        console.error('Axios Error:', error.response?.data);
      } else {
        setErrorText('알 수 없는 오류');
        console.error('Unknown Error:', error);
      }
    }
  };

  const closeVanCancelModal = () => {
    setShowVanCancelModal(false);
    setErrorText('');
  };

  const toggleDetail = (id: number) => {
    setOpenDetailId((prev) => (prev === id ? null : id));
  };

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [inView]);

  if (!showPaymentHistoryModal) return null;

  return (
    <>
      <Offcanvas
        show={showPaymentHistoryModal}
        onHide={showPaymentHistoryModalHandleClose}
        id="payment_history_modal_container"
        placement="bottom"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>결제 내역</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div className="total_sales_lists_wrapper">
            {!isFetchingNextPage && visibleSalesLists?.length === 0 ? (
              <div className="no_data">데이터가 없습니다.</div>
            ) : (
              <>
                {visibleSalesLists?.map((list, index) => (
                  <ul key={index} className="total_sales_lists_item">
                    <TotalSalesLists
                      showDetailPayment={() => null}
                      item={list}
                      showMileage={showMileage}
                      cancelBtn={
                        list.method === PAYMETHOD.CARD && authority !== AUTH.USER ? (
                          <div className="bottom_btn_wrapper">
                            <Button
                              className="van_cancel_btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCancelData(list);
                                setShowVanCancelModal(true);
                              }}
                              disabled={!vanCancelStatusQueries.find((q) => q.data)?.data}
                            >
                              {vanCancelStatusQueries.find((q) => q.data)?.data
                                ? '결제 취소'
                                : '결제 취소 완료'}
                            </Button>
                          </div>
                        ) : null
                      }
                    />

                    <Button
                      variant="outline-secondary"
                      className={`detail_toggle_btn ${openDetailId === list.id ? 'open' : ''}`}
                      size="sm"
                      onClick={() => toggleDetail(list.id)}
                    >
                      {openDetailId === list.id ? (
                        <div className="detail_toggle_btn_wrapper">
                          <span className="detail_toggle_btn_text">상세 닫기</span>
                          <span className="detail_toggle_btn_icon_wrapper">
                            <GoTriangleUp className="detail_toggle_btn_icon" />
                          </span>
                        </div>
                      ) : (
                        <div className="detail_toggle_btn_wrapper">
                          <span className="detail_toggle_btn_text">상세 보기</span>
                          <span className="detail_toggle_btn_icon_wrapper">
                            <GoTriangleUp className="detail_toggle_btn_icon rotate" />
                          </span>
                        </div>
                      )}
                    </Button>

                    <div
                      className={`sales_detail_text_wrapper ${
                        openDetailId === list.id ? 'open' : 'closed'
                      }`}
                    >
                      <div className="sales_date">
                        <span className="title">매출일자</span>
                        <span className="text">{formatTransactionDate(list.transactionDate)}</span>
                      </div>
                      <div className="sales_date">
                        <span className="title">매출시간</span>
                        <span className="text">
                          {formatTransactionDate(list.transactionDate)[1]}
                        </span>
                      </div>
                      <div className="sales_date time">
                        <span className="title">지점명</span>
                        <span className="text">{storeInfoState.currentStore}</span>
                      </div>
                      <div className="sales_date">
                        <span className="title">결제수단</span>
                        <span className="text">
                          {formatPayMethodLabel(showMileage, list.method, list.transactionStatus)}
                        </span>
                      </div>
                      <div className="sales_date">
                        <span className="title">매입사</span>
                        <span className="text">{list.cardCompany}</span>
                      </div>

                      <div className="sales_date">
                        <span className="title">승인번호</span>
                        <span className="text">{list.approvalNum || '0'}</span>
                      </div>

                      <div className="sales_date info">
                        <span className="title">카드번호</span>
                        <span className="text">{list.cardNum}</span>
                      </div>
                      <div className="sales_date">
                        <span className="title">금액</span>
                        <span className="text">{addCommas(list.amount || 0)}</span>
                      </div>
                      <div className="sales_date">
                        <span className="title">부가세</span>
                        <span className="text">{addCommas(list.tax || 0)}</span>
                      </div>

                      <div className="sales_date">
                        <span className="title">단말기번호</span>
                        <span className="text">{list.catId}</span>
                      </div>
                      <div className="sales_date">
                        <span className="title">거래유형</span>
                        <span className="text">
                          {formattedTransaction(list.transactionStatus) || ''}
                        </span>
                      </div>
                    </div>
                  </ul>
                ))}
                {isFetchingNextPage || isFetching ? (
                  <TotalSalesListsSkeleton />
                ) : (
                  <div ref={ref} className="targetRef"></div>
                )}
              </>
            )}

            {visibleSalesLists?.length > 0 && !hasNextPage && (
              <div className="last_page">마지막 페이지 입니다.</div>
            )}
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      <Modal
        show={showVanCancelModal}
        onHide={closeVanCancelModal}
        size="lg"
        centered
        id={'van_cancel_modal_container'}
      >
        <Modal.Body>
          <div className="van_cancel_modal_text_wrapper">
            <p>정말 취소 하시겠습니까?</p>
            <div>취소 후 복구 불가능 합니다.</div>
          </div>
          <ul className="van_cancel_modal_list">
            <li>90일 이내의 거래건만 취소가 가능합니다.</li>
            <li>카드 거래만 취소가 가능합니다.</li>
          </ul>

          <div className="van_cancel_modal_error_text">{errorText}</div>
        </Modal.Body>
        <Modal.Footer className="van_cancel_modal_footer">
          <Button
            className="van_cancel_btn"
            onClick={() => handleVanCancel(cancelData?.id || 0, cancelData?.vanKey || '')}
          >
            확인
          </Button>
          <Button className="cancel_btn" onClick={() => setShowVanCancelModal(false)}>
            취소
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PaymentHistoryModal;
